import { type Principal, type Role, type GeneralAccessLevel } from '../controlled/commonTypes';

import type { QueriesState } from './Controller';
import type { MasterState } from './masterState';

/** Data about a principal that you might want to look up by principal id */
export type PrincipalsMapEntry = {
	role: Role;
	principal: Principal;
	isNewOrUpdatedPrincipal: boolean;
};

export type PrincipalsMap = Map<string, PrincipalsMapEntry>;

/**
 * Builds a map that translates a principal ID to their profile and effective
 * permissions.
 */
export function makePrincipalsMap(
	{
		specificAccessDelta: { stagedAdditions, removals, reassignments, removeAll },
		generalAccessDelta,
	}: MasterState,
	{ contentRestrictions, currentUser, principalsPendingAccess }: QueriesState,
	{
		isCtaClick,
		stagedGeneralAccessLevel,
	}: { isCtaClick: boolean; stagedGeneralAccessLevel: GeneralAccessLevel },
): PrincipalsMap {
	const map = new Map<string, PrincipalsMapEntry>();

	// Ensure the current user is added to the principal map for these access levels.
	// This ensures the specific access list includes the current user when general access changes are staged.
	if (
		!(generalAccessDelta === 'none') &&
		(stagedGeneralAccessLevel === 'open-can-view' || stagedGeneralAccessLevel === 'restricted')
	) {
		map.set(currentUser.id, {
			role: 'can-edit',
			principal: currentUser,
			isNewOrUpdatedPrincipal: false,
		});
	}

	// Start with all the pre-existing principals
	for (const { principal, role } of contentRestrictions.principals) {
		map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: false });
	}

	// Include principals pending access
	for (const { principal, role } of principalsPendingAccess) {
		map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: false });
	}

	// Update the roles for any in the reassignments list
	for (const [id, role] of reassignments) {
		const existingEntry = map.get(id);
		if (existingEntry) {
			map.set(id, { ...existingEntry, role, isNewOrUpdatedPrincipal: true });
		}
	}

	// Delete all the ones removed in this session
	for (const id of removals) {
		map.delete(id);
	}

	// Remove all the principals in this session
	if (removeAll) {
		for (const { principal } of contentRestrictions.principals) {
			if (principal.id !== currentUser.id) {
				map.delete(principal.id);
			}
		}
		for (const { principal } of principalsPendingAccess) {
			if (principal.id !== currentUser.id) {
				map.delete(principal.id);
			}
		}
	}

	// Add the ones we've added in this session when CTA is clicked
	if (isCtaClick) {
		for (const { principal, role } of stagedAdditions) {
			map.set(principal.id, { role, principal, isNewOrUpdatedPrincipal: true });
		}
	}

	return map;
}
